// Turning a capture into something a cell can render.
//
// Shared by the explorer, the summary block on the bucket page and the charts'
// tooltips, so the three cannot drift apart — they describe the same rows and
// should read the same. datum's equivalent
// (routes/points/point/captures/format.js) flattens to text because a point
// owner's table has to render a capture from any point; this one knows the
// shape and produces named typed columns instead. See the note on
// /Public/Capture/Listing in common/schema/capture/public.js.

const byName = (items) =>
  (items || []).reduce((res, { name, value }) => {
    res[name] = value;
    return res;
  }, {});

// A measure absent from the row is 0, not undefined — an empty cell where a
// number belongs reads as "no data" when it means "none of that".
const measure = (values, name) => {
  const value = values[name];
  return typeof value === "number" ? value : Number(value || 0);
};

export const asListingRow = (item) => {
  const dimensions = byName(item.dimensions);
  const measures = byName(item.measures);

  return {
    ...item,
    ext: dimensions.ext || "",
    size: measure(measures, "size"),
    totalSize: measure(measures, "total-size"),
    derivedFileSize: measure(measures, "derived-file-size"),
    derivedFileCount: measure(measures, "derived-file-count"),
  };
};

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

// Signed, and that is the whole point of having it. Every measure on this path
// is a DELTA — the remove handler writes the file's sizes negated — so a
// formatter that dropped the sign would turn a removal into an addition.
export const formatBytes = (value, { signed = false } = {}) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "—";
  if (number === 0) return "0 B";

  const sign = number < 0 ? "-" : signed ? "+" : "";
  let magnitude = Math.abs(number);
  let unit = 0;

  while (magnitude >= 1024 && unit < UNITS.length - 1) {
    magnitude /= 1024;
    unit += 1;
  }

  // One decimal above bytes, none for bytes themselves — "1.5 KB" is useful,
  // "1536.0 B" is not.
  const rendered = unit === 0 ? String(Math.round(magnitude)) : magnitude.toFixed(1);
  return `${sign}${rendered} ${UNITS[unit]}`;
};

export const formatCount = (value, { signed = false } = {}) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "—";
  const sign = number > 0 && signed ? "+" : "";
  return `${sign}${number}`;
};
